import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import usePopupApi from "hooks/api/private/usePopupApi";

import { PopupBody, PopupDetails } from "types/api/popup";

import { getExtraErrorsDescription } from "utils/validators";

const POPUP_ID = "00000000-0000-0000-0000-000000000001";

export type PopupFormType = {
    enabled: boolean;
    text: {
        it: {
            title: string;
            description: string;
        };
        en: {
            title: string;
            description: string;
        };
    };
};

export type PopupFormHook = {
    formHook: FormHookType<PopupFormType>;
};

function usePopupForm(): PopupFormHook {
    const { updatePopup, getOnePopup } = usePopupApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                enabled: { type: "simple", validators: [] },
                text: {
                    type: "object",
                    properties: {
                        it: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [notEmptyValidator] },
                                description: { type: "simple", validators: [] },
                            },
                        },
                        en: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [] },
                                description: { type: "simple", validators: [] },
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: PopupFormType = React.useMemo(
        () => ({
            enabled: false,
            text: {
                it: { title: "", description: "" },
                en: { title: "", description: "" },
            },
        }),
        [],
    );

    const formHook = useForm<PopupFormType>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const [popupDetails, setPopupDetails] = React.useState<PopupDetails | undefined>(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOnePopup({ id: POPUP_ID });

        apiCall.then(setPopupDetails);

        return () => { abortController.abort(); };
    }, [getOnePopup]);

    React.useEffect(() => {
        if (popupDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.enabled = popupDetails.enabled;
                newForm.text.it.title = popupDetails.text.it?.title ?? "";
                newForm.text.it.description = popupDetails.text.it?.description ?? "";
                newForm.text.en.title = popupDetails.text.en?.title ?? "";
                newForm.text.en.description = popupDetails.text.en?.description ?? "";

                return newForm;
            });
        }
    }, [setForm, popupDetails]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("usePopupForm", () => {
            if (isValid) {
                const body: PopupBody = {
                    enabled: form.enabled,
                    text: {
                        it: {
                            title: form.text.it.title,
                            description: form.text.it.description,
                        },
                        en: {
                            title: form.text.en.title,
                            description: form.text.en.description,
                        },
                    },
                };

                const [apiCall] = updatePopup({ id: POPUP_ID }, body);
                return apiCall;
            } else {
                showAllError();
                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [addSaveAction, showAllError, updatePopup, isValid, form]);

    return React.useMemo(
        (): PopupFormHook => ({
            formHook,
        }),
        [formHook],
    );
}

export default usePopupForm;
